import { FormName } from '@core/data-access/models/form/form.model';
import { BuyerFormsSyncActions, FormFieldSyncActions } from '@core/data-access/store/form/form.actions';
import { setTicketHolderFormFields } from '@core/data-access/store/form/form.reducer';
import { AnonymousVoucherActions } from '@core/data-access/store/shared/shared.actions';
import { TicketAssignmentStoreState } from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import {
  changeBuyerInformationPersonBackToSavedBuyerPerson,
  createTicketHolderBuyerPerson,
  updateBuyerTicketHolderToSavedBuyerTicketHolder
} from '@core/features/ticket-assignment/store/reducers/buyer-ticket-holder.reducer';
import {
  addTicketHolderFormsAndQuestionnaireDynamicFields,
  removeTicketHolderForms,
  setTicketHolderFormsAndQuestionnaireDynamicFieldsOnAddedFeature
} from '@core/features/ticket-assignment/store/reducers/ticket-holder-form.reducer';
import {
  addTicketHolderQuestionnaireFormOnTicketHolderAssign,
  addTicketHolderQuestionnaireFormsOnSendingOptionChange,
  removeTicketHolderQuestionnaireFormOnTicketHolderAssign,
  removeTicketHolderQuestionnaireFormsOnSendingOptionChange,
  resetTicketHolderQuestionnaireDynamicFields,
  setTicketHolderQuestionnaireDynamicFields
} from '@core/features/ticket-assignment/store/reducers/ticket-holder-questionnaire-form.reducer';
import {
  setSendToOwnerFormField,
  setTicketHolderSendingOptionFormFields,
  setTicketHolderSendToOwnerFormFields
} from '@core/features/ticket-assignment/store/reducers/ticket-holder-sending-option.reducer';
import {
  addNewTicketHolder,
  addTicketHolderAndSetFormFields,
  assignTicketHolder,
  setTicketHolderFields,
  unassignDeletedTicketHolder,
  updateTicketHoldersToSavedTicketHolders
} from '@core/features/ticket-assignment/store/reducers/ticket-holder.reducer';
import {
  TicketAssignmentActions,
  TicketHolderActions,
  TicketHolderDynamicFieldsActions,
  TicketHolderFormActions
} from '@core/features/ticket-assignment/store/ticket-assignment.actions';
import { OrderSummaryActions } from '@features/order-summary/data-access/store/order-summary.actions';
import { createReducer, on } from '@ngrx/store';
import { SendingOptionActions } from '@store/products/product-selection/product-selection.actions';
import { isEmpty } from 'lodash';

export const ticketAssignmentReducerKey = 'ticketAssignment';

export const initialTicketAssignmentStoreState: TicketAssignmentStoreState = {
  selectedTariffUuid: null,
  ticketHolders: {},
  dynamicFields: {
    [FormName.TicketHolderQuestionnaire]: {}
  },
  forms: {
    [FormName.TicketHolder]: {},
    [FormName.TicketHolderQuestionnaire]: {}
  }
};

export const ticketAssignmentReducer = createReducer(
  initialTicketAssignmentStoreState,
  on(
    TicketAssignmentActions.setTicketHolderFormsAndQuestionnaireDynamicFieldsOnAddedFeature,
    (state, { tariffUuids, ticketHolderQuestionnaireDynamicFields }) =>
      setTicketHolderFormsAndQuestionnaireDynamicFieldsOnAddedFeature(
        state,
        tariffUuids,
        ticketHolderQuestionnaireDynamicFields
      )
  ),
  on(
    TicketAssignmentActions.addTicketHolderFormsAndQuestionnaireDynamicFields,
    (state, { tariffUuids, ticketHolderQuestionnaireDynamicFields }) =>
      addTicketHolderFormsAndQuestionnaireDynamicFields(state, tariffUuids, ticketHolderQuestionnaireDynamicFields)
  ),
  on(TicketAssignmentActions.removeTicketHolderForms, (state, { tariffUuids }) =>
    removeTicketHolderForms(state, tariffUuids)
  ),
  on(TicketAssignmentActions.addTicketHolderQuestionnaireFormOnTicketHolderAssign, (state, { person, formFields }) =>
    addTicketHolderQuestionnaireFormOnTicketHolderAssign(state, person, formFields)
  ),
  on(TicketAssignmentActions.removeTicketHolderQuestionnaireFormOnTicketHolderAssign, (state) =>
    removeTicketHolderQuestionnaireFormOnTicketHolderAssign(state)
  ),
  on(
    TicketAssignmentActions.addTicketHolderQuestionnaireFormsOnSendingOptionChange,
    (state, { tariffUuids, savedTicketHolders }) =>
      addTicketHolderQuestionnaireFormsOnSendingOptionChange(state, tariffUuids, savedTicketHolders)
  ),
  on(TicketAssignmentActions.removeTicketHolderQuestionnaireFormsOnSendingOptionChange, (state, { tariffUuids }) =>
    removeTicketHolderQuestionnaireFormsOnSendingOptionChange(state, tariffUuids)
  ),
  on(
    TicketHolderDynamicFieldsActions.setTicketHolderQuestionnaireDynamicFields,
    (state, { ticketHolderQuestionnaireDynamicFields }) =>
      setTicketHolderQuestionnaireDynamicFields(state, ticketHolderQuestionnaireDynamicFields)
  ),
  on(TicketHolderDynamicFieldsActions.resetTicketHolderQuestionnaireDynamicFields, (state) =>
    resetTicketHolderQuestionnaireDynamicFields(state)
  ),
  on(TicketAssignmentActions.setSelectedTariffUuid, (state, { tariffUuid }) =>
    isEmpty(tariffUuid) ? state : { ...state, selectedTariffUuid: tariffUuid }
  ),
  on(TicketHolderActions.addNewTicketHolder, (state, { person }) => addNewTicketHolder(state, person)),
  on(TicketHolderActions.addTicketHolderAndSetFormFields, (state, { person, formFields }) =>
    addTicketHolderAndSetFormFields(state, person, formFields)
  ),
  on(TicketHolderActions.assignTicketHolder, (state, { person, formFields }) =>
    assignTicketHolder(state, person, formFields)
  ),
  on(TicketHolderActions.unassignDeletedTicketHolder, (state, { person }) =>
    unassignDeletedTicketHolder(state, person)
  ),
  on(TicketHolderActions.updateBuyerTicketHolderToSavedBuyerTicketHolder, (state, { person, formFields }) =>
    updateBuyerTicketHolderToSavedBuyerTicketHolder(state, person, formFields)
  ),
  on(TicketHolderActions.updateTicketHoldersToSavedTicketHolders, (state, { persons, removeTicketHolders }) =>
    updateTicketHoldersToSavedTicketHolders(state, persons, removeTicketHolders)
  ),
  on(TicketHolderActions.setTicketHolderFields, (state, { person, formFields }) =>
    setTicketHolderFields(state, person, formFields)
  ),
  on(FormFieldSyncActions.setTicketHolderFormFields, (state, { person, formFields }) =>
    setTicketHolderFormFields(state, person, formFields, FormName.TicketHolder)
  ),
  on(FormFieldSyncActions.setTicketHolderQuestionnaireFormFields, (state, { person, formFields }) =>
    setTicketHolderFormFields(state, person, formFields, FormName.TicketHolderQuestionnaire, true)
  ),
  on(TicketHolderFormActions.setSendToOwnerFormField, (state, { tariffUuid, formFields }) =>
    setSendToOwnerFormField(state, tariffUuid, formFields)
  ),
  on(OrderSummaryActions.setTicketHolderSendingOptionFormFields, (state, { tariffUuid, formFields }) =>
    setTicketHolderSendingOptionFormFields(state, tariffUuid, formFields)
  ),
  on(SendingOptionActions.setSendingOptions, (state, { sendingOptions }) =>
    setTicketHolderSendToOwnerFormFields(state, sendingOptions)
  ),
  on(BuyerFormsSyncActions.createTicketHolderBuyerPerson, (state, { createdPerson, savedPerson, formFields }) =>
    createTicketHolderBuyerPerson(state, createdPerson, savedPerson, formFields)
  ),
  on(
    BuyerFormsSyncActions.changeBuyerInformationPersonBackToSavedBuyerPerson,
    (state, { currentPerson, savedPerson, formFields }) =>
      changeBuyerInformationPersonBackToSavedBuyerPerson(state, currentPerson, savedPerson, formFields)
  ),
  on(
    AnonymousVoucherActions.resetStateOnAnonymousVoucherRedeem,
    TicketAssignmentActions.resetReducer,
    () => initialTicketAssignmentStoreState
  )
);
